// The cursor drain: walk GET /api/notes to its last page, one request in
// flight per scope, each page committed with its cursor advance. Mirrors the
// Hub's page-number walk (notes-hub/index.vue drainNotes/loadMoreNotes) in the
// cursor world: a second caller joins the page in flight and is told the rows
// it added; a page that makes no progress ends the walk; a page a later walk
// overtook commits nothing and leaves loaded/total alone. Retry and backoff
// stay with the caller (the Hub owns its timers), so this module has none.
// docs/plans/2026-09-02-next-steps-e2e-encryption.md, section 6.7; #1548.
import { isQuotaError } from './idb';
import { scopeKey } from './schema';

const flightKey = (memberId, scope) => `${memberId}|${scopeKey(scope)}`;

// A page answered stale under a walk's own generation means another context
// (a second tab) is walking the same scope; after this many in a row the walk
// yields to it rather than re-asking forever.
const MAX_STALE_STREAK = 3;

export function createDrainer(store) {
  const inFlight = new Map();
  const walks = new Map();

  const walkState = key => {
    if (!walks.has(key)) walks.set(key, { current: 0, stopped: false });
    return walks.get(key);
  };

  async function runPage(memberId, scope, fetchPage) {
    let meta;
    try {
      meta = await store.getMeta(memberId, scope);
    } catch (error) {
      return { ok: false, reason: 'storage', error, loaded: 0, total: 0 };
    }
    const base = { loaded: meta.loaded, total: meta.total, added: 0, written: 0 };
    if (meta.complete)
      return { ok: true, noop: true, complete: true, stale: false, stalled: false, ...base };

    let page;
    try {
      page = await fetchPage({
        cursor: meta.cursor,
        view: scope.view,
        program: scope.program,
        scope,
      });
    } catch (error) {
      return { ok: false, reason: 'fetch', error, ...base };
    }
    const items = (Array.isArray(page?.items) ? page.items : []).filter(
      item => item?.id !== undefined && item?.id !== null,
    );
    const nextCursor = page?.nextCursor ?? null;
    // With a cursor, a page's rows always lie beyond the rows held, so "adds
    // nothing" cannot be read off the ids: what bounds the walk is the cursor
    // moving. An empty page that still claims a next cursor, or a next cursor
    // equal to the one sent, is the server's inconsistency; nothing is written.
    const stalled = nextCursor !== null && (items.length === 0 || nextCursor === meta.cursor);
    if (stalled) return { ok: true, stalled: true, stale: false, complete: false, ...base };

    let outcome;
    try {
      const db = await store.open(memberId);
      outcome = await store.writePage(db, scope, {
        generation: meta.generation,
        cursorSent: meta.cursor,
        items,
        nextCursor,
        total: page?.total,
      });
    } catch (error) {
      // A write that fails must not leave a `complete` behind it. It is
      // already false during a walk; clearing again is what makes that true
      // whatever else happened, and it costs one tiny transaction.
      await store.clearComplete(memberId, scope).catch(() => {});
      return { ok: false, reason: isQuotaError(error) ? 'quota' : 'storage', error, ...base };
    }
    if (outcome.stale) return { ok: true, stale: true, stalled: false, complete: false, ...base };
    return {
      ok: true,
      stale: false,
      stalled: false,
      complete: outcome.meta.complete,
      added: outcome.added,
      written: outcome.written,
      loaded: outcome.meta.loaded,
      total: outcome.meta.total,
      nextCursor,
    };
  }

  /**
   * Fetch and commit the next page of a scope. One request in flight per
   * scope: a caller arriving while one is open is handed THAT request's
   * result, so both are told the same `added`. Resolves, never rejects:
   * `{ ok: true, added, written, complete, stale, stalled, loaded, total }`
   * or `{ ok: false, reason: 'fetch' | 'quota' | 'storage', error }`.
   */
  function fetchNextPage(memberId, scope, { fetchPage }) {
    const key = flightKey(memberId, scope);
    if (inFlight.has(key)) return inFlight.get(key);
    const run = runPage(memberId, scope, fetchPage).finally(() => inFlight.delete(key));
    inFlight.set(key, run);
    return run;
  }

  /**
   * Walk a scope to its last page, from wherever the stored cursor left it.
   * Starting a walk supersedes the one in flight for the scope. Resolves to
   * `{ ok: true, complete: true, loaded, total, pages }`,
   * `{ ok: true, complete: false, reason: 'stopped' | 'superseded' | 'stalled', ... }`
   * or `{ ok: false, reason: 'fetch' | 'quota' | 'storage', error, ... }`.
   */
  async function drain(memberId, scope, { fetchPage, onProgress } = {}) {
    const key = flightKey(memberId, scope);
    const state = walkState(key);
    state.current += 1;
    state.stopped = false;
    const mine = state.current;

    let meta;
    try {
      meta = await store.startWalk(memberId, scope);
    } catch (error) {
      const reason = isQuotaError(error) ? 'quota' : 'storage';
      return { ok: false, reason, error, loaded: 0, total: 0, pages: 0 };
    }
    let { loaded, total } = meta;
    let pages = 0;
    let staleStreak = 0;
    const summary = () => ({ loaded, total, pages });
    if (meta.complete) return { ok: true, complete: true, ...summary() };

    for (;;) {
      const page = await fetchNextPage(memberId, scope, { fetchPage });
      if (page.ok && !page.stale) ({ loaded, total } = page);
      if (page.ok && !page.stale && !page.stalled && !page.noop) {
        pages += 1;
        if (onProgress)
          await Promise.resolve(onProgress({ loaded, total, complete: page.complete })).catch(
            () => {},
          );
      }
      if (state.current !== mine) {
        return {
          ok: true,
          complete: false,
          reason: state.stopped ? 'stopped' : 'superseded',
          ...summary(),
        };
      }
      if (!page.ok) return { ok: false, reason: page.reason, error: page.error, ...summary() };
      if (page.complete) return { ok: true, complete: true, ...summary() };
      if (page.stalled) return { ok: true, complete: false, reason: 'stalled', ...summary() };
      staleStreak = page.stale ? staleStreak + 1 : 0;
      if (staleStreak >= MAX_STALE_STREAK)
        return { ok: true, complete: false, reason: 'superseded', ...summary() };
    }
  }

  /** End the walk in flight for a scope at its next check. The page in flight still lands. */
  function stop(memberId, scope) {
    const state = walkState(flightKey(memberId, scope));
    state.current += 1;
    state.stopped = true;
  }

  return { fetchNextPage, drain, stop };
}
